/**
 * Application-related TypeScript types and interfaces
 * Based on Liberty Rent API documentation
 */

/**
 * Address data structure for residence-specific information
 */
export interface AddressData {
  moveIn_date?: string // YYYY-MM-DD format
  moveOut_date?: string | null // YYYY-MM-DD when moving out; null clears when still living / on update
  currently_living?: boolean | string // API accepts "1" or "0" as string, or boolean
  // For RENT type
  landlord_name?: string | null
  landlord_mobile_no?: string | null // Unformatted phone number
  landlord_email?: string | null
  landlord_website?: string | null // Website URL
  landlord_relationship_type?: string | null
  paid_rent?: number | null
  rent_amount?: string | null // Monthly rent amount
  // For OWN type
  month_reside?: number | string | null // Number of months resided (API may return as string); null clears on update
}

/**
 * Current address structure matching API
 */
export interface Address {
  ra_id?: number | null // Resident Address ID (required for updates, null for new)
  street_address?: string // Full formatted address string
  outside_us?: boolean | string // true/"1" if address is outside US, false/"0" if inside US (API may return as string)
  house_no?: string // Street number
  street?: string // Street name
  street_type?: string // Street type (St, Ave, Rd, etc.)
  apartment?: string | null // Apartment/Unit number
  city?: string
  state?: string
  zip_code?: string
  country?: string // Country code (default: "US")
  address_type?: string // "RENT", "OWN", or "FAMILY"
  address_data?: AddressData
}

/**
 * Job/Employment information
 */
export interface JobInfo {
  job_id?: number
  employer_name?: string
  job_title?: string
  start_date?: string
  end_date?: string
  monthly_income?: number
}

/**
 * Current Employment API structure (matches actual API)
 * Also used for Previous Employment (previous_employment uses same structure)
 */
export interface CurrentEmployment {
  job_id?: number | string // Empty string "" for new, number for existing
  job_type?: string // "FULL_TIME", "PART_TIME", "SELF_EMPLOYED"
  employer_name?: string
  employer_street_address?: string // Full formatted address (for autocomplete)
  outside_us?: boolean | string // true/"1" if address is outside US, false/"0" if inside US (API may return as string)
  employer_street?: string // Street address
  employer_zip_code?: string | number // Zip code (max 6 digits)
  employer_city?: string
  employer_country?: string // Country code (default: "US")
  employer_state?: string // State (US state code or text for non-US)
  income?: string // Income amount (currency format, string)
  income_period?: string // "Per_Week", "Per_Bi_Monthly", "Per_Month", "Per_Year", or "One_Time"
  job_duration?: number // Duration in months (auto-calculated)
  job_start_date?: string // Start date (YYYY-MM-DD format)
  check_types?: string
  reference_contact?: string
  employer_website?: string // Website URL (optional, for SELF_EMPLOYED)
  hr_manager_contact_email?: string // HR/Manager contact email
  present_job?: string // "1" = Current active job, "0" = Ended (for current_employment)
  job_end_date?: string | null // End date (null if present_job = "1", required for previous_employment)
  distance_explain?: string // Explanation if job distance is far
  job_distance?: number | string // Distance from property (can be number or empty string "")
  // Fields specific to previous_employment
  job_gap?: number // Gap in months between jobs (auto-calculated, for previous_employment)
  job_gap_explanation?: string // Required if job_gap >= 1 month (for previous_employment)
}

/**
 * File attachment metadata returned on resident/application payloads
 */
export interface ApplicationFileAttachment {
  attachment_id?: number
  filename?: string
  name?: string
  filepath?: string
  type?: string
  created_at?: string | number
}

/**
 * Other Source of Income API structure (matches actual API)
 */
export interface OtherSource {
  job_id?: number | string // Empty string "" for new, number for existing
  source_name?: string // Name/description of the income source
  income?: string // Income amount (currency format, string)
  income_period?: string // "Per_Month" or "One_Time"
  job_start_date?: string // Start date (YYYY-MM-DD format) or Transaction Date for One_Time
  job_end_date?: string | null // End date (null if present_job = "1")
  present_job?: string // "1" = Currently active, "0" = Ended
  job_duration?: number | string // Duration in months (auto-calculated)
  check_types?: string // Document type for proof of income
  other_proof_of_income?: Array<{
    attachment_id?: number
    filename?: string
    filepath?: string
    type?: string
    created_at?: string
  }> // Array of uploaded proof of income files
  reference_contact?: string | null
}

/** Personal contact fields captured at registration (immutable About Yourself snapshot). */
export type PersonalInfoSnapshot = {
  first_name?: string
  middle_name?: string
  last_name?: string
  email?: string
  mobile_no?: string
  shared_phone_no?: string
  birthdate?: string
  security_number?: string
  communication_preference?: string
}

/**
 * User data within application
 */
/** Personal contact and property details shown on the Invite tab. */
export interface InviteRegistrationView {
  personal?: PersonalInfoSnapshot
  property?: {
    property_name?: string
    property_address?: string
    rent_amount?: number | string | null
    access_fee?: number | string | null
    balance_due?: number | string | null
    unit_number?: string
    lease_start_date?: string
    lease_duration?: string
    invite_code?: string
    agent_name?: string
    agent_email?: string
    expected_residents?: number | string | null
  }
}

export interface ApplicationUserData {
  first_name?: string
  middle_name?: string | null
  last_name?: string
  email?: string
  mobile_no?: string
  suffix?: string | null
  shared_phone_no?: string
  profile_picture?: string
}

/**
 * Awaiting reason child item (from API awaiting_reasons / awaiting_data)
 */
export interface AwaitingChildItem {
  label?: string
  description?: string
  inputvalue?: string
  personalized_message?: string
}

/**
 * Awaiting reason category (parsed from awaiting_reasons JSON)
 */
export interface AwaitingCategoryData {
  [categoryKey: string]: { label?: string; child?: AwaitingChildItem[] }
}

/**
 * Co-Resident/Co-Applicant data structure
 */
export interface CoResident {
  first_name: string
  middle_name?: string
  last_name: string
  email?: string
  mobile_no?: string
  relationship_type?: string
  spouse?: boolean
  invite_code?: string
  applicant_invite_code?: string
  applicant_id?: number
  applicant_status?: string
  applicant_lock?: string
  is_approved_require?: string | number
  [key: string]: unknown
}

/**
 * Previous application data (revisit scenario).
 * When a user starts a new application, the API may return oldapplication
 * with address/employment from their last application for optional import.
 */
export interface OldApplication {
  user_data?: ApplicationUserData
  current_address?: Address
  previous_address?: Address[]
  current_employment?: CurrentEmployment[]
  previous_employment?: CurrentEmployment[]
}

/**
 * Resident application data from API
 * Maps to /api/resident_application response
 */
export interface ResidentApplicationData {
  // IDs
  applicant_id?: number | string
  application_id?: number | string
  fk_application_id?: number | string // Alternative field name from API
  invite_id?: number
  user_id?: number
  property_id?: number
  fk_property_id?: number // API field for property save (pre-prod parity)
  fk_agent_id?: number // API field for agent (pre-prod parity)
  fk_progress_status_id?: number

  // Personal info
  first_name?: string
  applicant_first_name?: string // Alternative field name from API
  middle_name?: string | null
  last_name?: string
  applicant_last_name?: string // Alternative field name from API
  suffix?: string | null
  email?: string
  applicant_email?: string // Alternative field name from API
  mobile_no?: string
  applicant_mobile_no?: string // Alternative field name from API
  shared_phone_no?: string
  birthdate?: string
  security_number?: string
  communication_preference?: string
  expected_residents?: number | string
  rental_acknowledgement?: number | string | null
  rental_verification_consent_html?: string
  /** Immutable personal fields captured at registration. */
  personal_info_snapshot?: PersonalInfoSnapshot | null
  /** Read-only personal contact and property details captured at registration. */
  invite_registration?: InviteRegistrationView
  /** Organisation invite instructions shown on the Invite tab. */
  invite_intro_html?: string

  // Status
  status?: string
  applicant_status?: string
  application_status?: string
  invite_status?: string
  invite_accepted?: string
  activeButton?: string
  active?: boolean
  contract_paid?: boolean
  is_primary?: number
  credit_report?: string // '1' if created, '0' or undefined if not
  is_approved_require?: string | number
  allow_edit?: boolean // Backend flag to control edit permission
  applicant_lock?: string // '1' when application is locked (e.g. under review or awaiting co-applicant)
  awaiting_reasons?: string // JSON string from API; parsed into awaiting_data
  awaiting_data?: AwaitingCategoryData // Parsed awaiting reasons (categories with label and child items)
  awaiting_notes?: string // Optional HTML notes from awaiting_reasons JSON (sibling of awaiting_data)
  denied_checkbox_data?: Record<string, { key?: string; label?: string; child?: Array<{ key?: string; label?: string; description?: string; isLock?: number; inputvalue?: string }> }>
  co_resident_application?: CoResident[]
  co_resident?: CoResident[]

  // Property info
  property_name?: string
  unit_number?: string | null
  physicalAddress?: string
  rent_amount?: number
  application_fee?: number
  access_fee?: number
  paid_amount?: number
  balance_due?: number
  rent_added?: boolean | string | number
  application_fee_invoice?: boolean | string | number
  default_invoice?: boolean | string | number
  payment_failed_counter?: number | string
  surcharge_fee?: boolean | string | number
  apply_surcharge?: string | number

  // Dates
  lease_start_date?: string
  contract_end_date?: string | null
  invite_created_date?: number
  lease_duration?: string
  payment_due_date?: string // Payment due date in ISO format

  // Invite
  invite_code?: string
  app_invite_type?: string | null
  income_verified?: string | number
  income?: string | number

  // Address data
  current_address?: Address
  previous_address?: Address[]
  alternate_address?: Address[]
  non_us_address?: boolean | string // "1" or "0" as string, or boolean (API may return as string)

  // Revisit: previous application data for optional import (matches Angular oldapplication)
  oldapplication?: OldApplication

  // Employment data
  current_job?: JobInfo[]
  previous_job?: JobInfo[]
  current_employment?: CurrentEmployment[]
  previous_employment?: CurrentEmployment[]
  other_source?: OtherSource[] // Other sources of income
  /** API alias for other_source on some resident payloads */
  resident_other_source?: OtherSource[]
  employment_status?: string
  previous_employer?: string
  /** Explanation for a gap between employments (Angular: job_gap_description; gates prev-app import prompt) */
  job_gap_description?: string
  ninja_reason?: string
  underwriter_verified_income_other?: string
  underwriter_verified_income_type?: string
  enter_date?: Record<string, string> // Date strings for other_source entries (empty object allowed)

  // Bank linking data
  bank_name?: string
  bank_already_link?: boolean | string | number
  plaid_opt_out?: string // "1" or "0"
  ninja_income_proof?: ApplicationFileAttachment[]
  // General proof of income (Employment tab) – per README §6.2; shown when AWAITING or existing files
  proof_of_income?: ApplicationFileAttachment[]

  // Government ID files
  government_id?: ApplicationFileAttachment[]
  government_id_back?: ApplicationFileAttachment[]
  /** API typo field names for government ID attachments (single object or array) */
  goverment_id_attachment?: ApplicationFileAttachment | ApplicationFileAttachment[]
  goverment_id_attachment_back?: ApplicationFileAttachment | ApplicationFileAttachment[]

  // User data (from login response)
  user_data?: ApplicationUserData

  // History data
  evicted?: boolean | string | number
  has_been_evicted?: boolean | string | number
  rent_debt_collection?: string | number
  bankruptcy?: boolean | string | number
  is_bankrupt?: boolean | string | number
  involved_bankruptcy?: string | number
  bankruptcy_type?: string
  bankruptcy_settled?: boolean | string | number
  bankruptcy_explanation?: string
  number_of_dependents?: number | string
  dependents?: number | string
  children?: string | number
  // Angular API field names (matching Angular implementation)
  still_open_bankruptcy?: string | number // '1' = still open, '0' = settled (opposite of bankruptcy_settled)
  involved_bankruptcy_list?: string // Bankruptcy explanation (alternative to bankruptcy_explanation)
  involved_bankruptcy_date_to_settle?: string // Settlement date (shown when settled)

  // Signature data
  digital_signature?: string
  /** Fallback plain-text signature when digital_signature is absent */
  signature?: string
  all_links_read?: string | number | boolean
  access_statement?: string | number
  uc_optin?: string | number // "1" = opted in to Utility Concierge, "0" = not opted in
  signature_date?: string // Date when signature was submitted (read-only after signing)
  ip_address?: string // IP address from which the signature was submitted
  goverment_id_type?: string // "Drivers License" or "Passport" (Note: API uses this spelling)
  verifast_id_status?: string // pending|in_progress|complete|failed|needs_review
  verifast_completion_status?: string | null // latest Verifast session completion_status
  verifast_approval_status?: string | null
  verifast_acceptance_status?: string | null // PENDING|ACCEPTED|REJECTED|APPROVED
  verifast_id_verify_status?: string | null

  // Payment history
  payment_history?: PaymentHistoryItem[]
  payment_and_payment_adjustment?: PaymentHistoryItem[]
  payment_log_id?: number | null
  progress_status?: string
  progress_status_date?: string

  // Payment adjustments/invoices
  payment_adjustment?: PaymentAdjustmentItem[]

  // Nested objects from API
  property?: {
    property_id?: number
    property_name?: string
    property_address?: string
    property_address2?: string
    property_city?: string
    property_state?: string
    property_zipcode?: string
    listed?: string
    pmc_invite?: string
    disable_uc?: string
    lease_duration?: string // Lease Duration
    email?: string // Leasing Consultant/Property Manager Email
    mobile_no?: string // Leasing Consultant/Property Manager Phone
    [key: string]: unknown
  }
  agent?: {
    user_id?: number
    first_name?: string
    middle_name?: string | null
    last_name?: string
    email?: string
    mobile_no?: string
    suffix?: string | null
    shared_phone_no?: string | null
    [key: string]: unknown
  }
  other_agent?: {
    user_id?: number
    first_name?: string
    middle_name?: string | null
    last_name?: string
    email?: string
    mobile_no?: string
    suffix?: string | null
    shared_phone_no?: string | null
    [key: string]: unknown
  } | null
}

/**
 * Payment adjustment item from resident application API
 */
export interface PaymentAdjustmentItem {
  pa_id?: number
  adjustment_amount?: number | string
  adjustment_type?: string
  adjustment_reason?: string
  payment_status?: string
  transaction_id?: string
  created_at?: number | string
  fk_applicant_id?: number
  first_name?: string
  last_name?: string
  paid_payment?: number | null
  [key: string]: unknown
}

/**
 * Payment history item from API
 */
export interface PaymentHistoryItem {
  payment_log_id?: number
  auth_code?: string
  fk_applicant_id?: number
  payment_type?: string
  payment_amount?: string
  payment_source?: string
  payment_status?: string
  ip_address?: string
  created_at?: number | string
  transaction_id?: string
  fk_payment_log_id?: number | null
  transaction_status?: string | null
  special_note?: string | null
  gateway_status?: string
  gateway_amount?: string
  payment_log_refund_fee?: number | null
  fk_user_id?: number
  paid_by?: string
  payment_log_surcharge_fee?: number | null
}

/**
 * Ninja list response - detailed applicant data
 */
export interface NinjaListResponse {
  applicant_id?: number
  first_name?: string
  middle_name?: string | null
  last_name?: string
  suffix?: string | null
  email?: string
  mobile_no?: string
  birthdate?: string
  ssn?: string
  communication_preference?: string
  shared_phone?: string
  // Add more fields as needed based on actual API response
}

/**
 * Resident application API response
 */
export interface ResidentApplicationResponse {
  success?: boolean
  data?: ResidentApplicationData | ResidentApplicationData[]
  message?: string
}

/** Matches Laravel ApplicationController resident validation helpers */
export type ResidentValidationScope = 'section' | 'tab'
export type ResidentValidationTab =
  | 'about'
  | 'property'
  | 'income'
  | 'history'
  | 'signature'

/**
 * Update application request payload
 */
export interface UpdateApplicationRequest {
  first_name?: string
  middle_name?: string
  last_name?: string
  suffix?: string | null
  mobile_no?: string
  shared_phone_no?: string
  email?: string
  communication_preference?: string
  rental_acknowledgement?: string | number
  birthdate?: string
  security_number?: string
  current_address?: Address
  previous_address?: Address[]
  alternate_address?: Address[]
  enter_date?: Record<string, string> // Empty object or object with date strings
  non_us_address?: boolean | string // Optional: "1" or "0" as string, or boolean. true/"1" if all addresses are outside US
  property_name?: string
  property_state?: string
  rent_amount?: number | string // API accepts number or string (e.g. "900.00") per pre-prod
  lease_start_date?: string
  lease_duration?: string
  property_id?: number
  // Pre-prod / Angular parity: property tab payload
  applied_property?: string // "1" or "0"
  fk_property_id?: number
  fk_agent_id?: number
  other_individuals?: string // "1" or "0"
  current_job?: JobInfo[]
  previous_job?: JobInfo[]
  current_employment?: CurrentEmployment[]
  previous_employment?: CurrentEmployment[]
  other_source?: OtherSource[] // Other sources of income
  resident_other_source?: OtherSource[]
  employment_status?: string
  previous_employer?: string
  ninja_reason?: string
  underwriter_verified_income_other?: string
  underwriter_verified_income_type?: string
  co_resident?: CoResident[]
  // History data
  evicted?: string | number
  bankruptcy?: string | number
  bankruptcy_type?: string
  bankruptcy_settled?: string | number
  bankruptcy_explanation?: string
  number_of_dependents?: number | string
  checking?: string | number
  saving?: string | number
  creditcard?: string | number
  // API field names for history (matching Angular implementation)
  rent_debt_collection?: string
  rent_supporting_document?: string
  involved_bankruptcy?: string
  children?: string
  involved_bankruptcy_list?: string // Bankruptcy explanation
  still_open_bankruptcy?: string // '1' = still open, '0' = settled
  involved_bankruptcy_date_to_settle?: string // Settlement date
  validation?: boolean
  /** section = one-section save; tab = full tab / save & continue (backend ApplicationController) */
  validation_scope?: ResidentValidationScope
  validation_tab?: ResidentValidationTab
  /** Admin Application Detail property tab — backend property validation branch */
  from_admin?: boolean
  // Deletion tracking
  deleted?: {
    resident_job?: number[] // Array of job_id values to delete
    alternate_address?: number[] // Array of ra_id values to delete
    proof_of_income?: (number | string)[] // Attachment IDs to delete (README §6.5)
  }
  is_mobile_no?: string // "1" if mobile number exists, "0" otherwise
  update_via_sms?: string // "0" or "1" for SMS update preference
  // Signature data
  digital_signature?: string // JSON string: "{\"signature\":\"...\",\"signaturetype\":\"type1\"}"
  access_statement?: string | number // "1" or "0"
  uc_optin?: string | number // "1" = opted in to Utility Concierge, "0" = not
  all_links_read?: string | number | boolean // "1" or "0"
  goverment_id_type?: string // "Drivers License" or "Passport"
  verifast_id_status?: string // pending|in_progress|complete|failed|needs_review
  verifast_acceptance_status?: string | null
  invite_code?: string
  // Submit for processing (AWAITING workflow)
  request_approval?: boolean
  data_not_change?: string // "1" when progress is "Awaiting External" and no changes
}

/** Partial section save — backend skips the legacy “all scalars required” check. `validation_tab` is optional metadata (PHP uses scope for the main branch). */
export function residentSectionValidation(
  tab: ResidentValidationTab
): Pick<UpdateApplicationRequest, 'validation_scope' | 'validation_tab'> {
  return { validation_scope: 'section', validation_tab: tab }
}

/**
 * Full tab / Save & Continue — backend runs tab-specific rules in validateResidentApplicationPayload.
 * Includes `validation: true` because only `section` is exempt from that legacy loop; `tab` is not.
 */
export function residentTabValidation(
  tab: ResidentValidationTab
): Pick<UpdateApplicationRequest, 'validation_scope' | 'validation_tab' | 'validation'> {
  return { validation_scope: 'tab', validation_tab: tab, validation: true }
}

/**
 * Application admin (embedded ApplicationDetail) tab save — mirrors Angular
 * `resident-application-add.component.ts#submitForm` `!this.fromRoute` branch.
 * Angular only sets `validation_scope` + `validation_tab` here (NO `validation` flag);
 * applicant-facing pages still use `residentTabValidation`.
 */
export function applicationTabValidation(
  tab: Extract<ResidentValidationTab, 'about' | 'income'>
): Pick<UpdateApplicationRequest, 'validation_scope' | 'validation_tab'> {
  return { validation_scope: 'tab', validation_tab: tab }
}

/**
 * Application admin Property tab save — mirrors Angular `!fromRoute` branch:
 * `validation_scope` + `validation_tab: 'property'` + `from_admin: true`.
 */
export function applicationPropertyTabValidation(): Pick<
  UpdateApplicationRequest,
  'validation_scope' | 'validation_tab' | 'from_admin'
> {
  return {
    validation_scope: 'tab',
    validation_tab: 'property',
    from_admin: true,
  }
}

/**
 * Income / employment tab — `validation_scope`, `validation_tab: 'income'`.
 * Section saves: no `validation` key (same as legacy `validation: false` → scope section in PHP).
 * Tab saves: `validation: true` so ApplicationController skips the legacy “all scalars required” loop
 * (see `getResidentValidationScope` / `filled('validation')` branch).
 */
export function incomeValidationHints(
  scope: 'section' | 'tab'
): Pick<UpdateApplicationRequest, 'validation_scope' | 'validation_tab' | 'validation'> {
  if (scope === 'tab') {
    return { validation_scope: 'tab', validation_tab: 'income', validation: true }
  }
  return { validation_scope: 'section', validation_tab: 'income' }
}

/**
 * Application state for Redux
 */
export interface ApplicationState {
  applicationData: ResidentApplicationData | null
  applications: ResidentApplicationData[]
  isLoading: boolean
  error: string | null
  isReadOnly: boolean
  /** True when user has multiple active applications and must select one from dashboard */
  hasMultipleActiveApplications: boolean
  /** True when user has explicitly selected an application from the dashboard */
  hasSelectedApplication: boolean
}

/**
 * Check if application fee has been paid
 * Checks payment_history array for completed APPLICATION_FEE payments
 * Based on AngularJS implementation: checkApplicationPaid() method
 * @param applicationData - Application data
 * @returns True if application fee has been paid
 */
export function checkApplicationPaid(applicationData: ResidentApplicationData | null | undefined): boolean {
  if (!applicationData || !applicationData.payment_history || !Array.isArray(applicationData.payment_history)) {
    return false
  }

  let totalApplicationPayment = 0.0
  const applicantId = applicationData.applicant_id

  applicationData.payment_history.forEach((paymentDetail) => {
    if (
      paymentDetail.payment_type &&
      paymentDetail.payment_type.includes('APPLICATION_FEE') &&
      paymentDetail.payment_status === 'Completed' &&
      String(paymentDetail.fk_applicant_id) === String(applicantId)
    ) {
      const paymentAmount = paymentDetail.payment_amount ? Number(paymentDetail.payment_amount) : 0
      totalApplicationPayment += paymentAmount
    }
  })

  return totalApplicationPayment > 0
}

/**
 * Check if payment has been added to application
 * Based on AngularJS implementation: isPaymentAdded flag
 * Enhanced to check specifically for current applicant's fee if in APPLICATION_FEE mode
 * @param applicationData - Application data
 * @returns True if payment has been added
 */
export function isPaymentAdded(applicationData: ResidentApplicationData | null): boolean {
  if (!applicationData) return false

  // If a payment_log_id exists, it's a strong indicator a payment was made
  const hasLogId = !!applicationData.payment_log_id

  // For application fees, we must ensure it's specifically for THIS applicant
  // This prevents co-applicants from seeing "Thank You" messages meant for the primary
  const applicantId = applicationData.applicant_id
  if (applicantId && applicationData.payment_history) {
    const hasOwnFeePayment = applicationData.payment_history.some(p =>
      p.payment_type &&
      p.payment_type.includes('APPLICATION_FEE') &&
      String(p.fk_applicant_id) === String(applicantId) &&
      (p.payment_status === 'Completed' || p.gateway_status === 'Authorized')
    )

    // If we're looking for application fee added, use the history check
    // If it's a contract payment, the presence of rent_added/balance_due handles visibility usually
    return hasOwnFeePayment || (hasLogId && !applicationData.payment_history.some(p => p.payment_type?.includes('APPLICATION_FEE')))
  }

  return hasLogId
}

/**
 * Get application status
 * @param applicationData - Application data
 * @returns Application status string (normalized to uppercase for consistency)
 */
export function getApplicationStatus(applicationData: ResidentApplicationData | null | undefined): string {
  if (!applicationData) return ''
  const status = (
    applicationData.applicant_status ||
    applicationData.application_status ||
    applicationData.status ||
    ''
  )
  // Normalize to uppercase for consistent comparison
  return status ? status.toUpperCase() : ''
}

/**
 * Check if application is read-only
 * Priority: allow_edit flag > status (PENDING/APPROVED/DENIED/CANCELLED/EXPIRED) > progress_status (credit-report processing states)
 * Based on AngularJS implementation and workflow documentation
 * @param applicationData - Application data
 * @returns True if application is read-only
 */
export function isApplicationReadOnly(applicationData: ResidentApplicationData | null): boolean {
  if (!applicationData) return false

  const status = getApplicationStatus(applicationData)

  // AWAITING and VOID: editable when allow_edit is not false (per AWAITING workflow doc).
  // Ignore applicant_lock so applicant can always update and resubmit when status is AWAITING/VOID.
  if (status === 'AWAITING' || status === 'VOID') {
    if (applicationData.allow_edit === false) return true
    return false
  }

  // When allow_edit is false, form is read-only
  if (applicationData.allow_edit === false) {
    return true
  }

  // PENDING, APPROVED, DENIED, CANCELLED, EXPIRED are read-only
  const readOnlyStatuses = ['PENDING', 'APPROVED', 'DENIED', 'CANCELLED', 'EXPIRED']
  if (readOnlyStatuses.includes(status)) {
    return true
  }

  // Credit-report processing progress statuses: application has been submitted for screening,
  // applicant should not be able to edit even if applicant_status is still INCOMPLETE.
  const progressStatus = (applicationData.progress_status || '').toUpperCase()
  const readOnlyProgressStatuses = [
    'CREDIT_REPORT_PENDING',
    'CREDIT_REPORT_READY',
    'CREDIT_REPORT_FAILED',
  ]
  if (readOnlyProgressStatuses.includes(progressStatus)) {
    return true
  }

  return false
}

/**
 * Returns true if the application is in a terminal/inactive state where we should
 * show the "under process" / inactive message on the Payment page.
 * Excludes the case where read-only is only because the application fee was paid.
 * @param applicationData - Application data
 * @returns True if we should show the inactive-application message
 */
export function isApplicationInactiveForPayments(applicationData: ResidentApplicationData | null): boolean {
  if (!applicationData) return false
  if (applicationData.allow_edit === false) return true
  const status = getApplicationStatus(applicationData)
  const inactiveStatuses = ['PENDING', 'APPROVED', 'DENIED', 'CANCELLED', 'EXPIRED']
  return inactiveStatuses.includes(status)
}

/**
 * Format phone number for display
 * @param phone - Raw phone number
 * @returns Formatted phone number
 * Formats as:
 * - 10 digits: (XXX) XXX-XXXX
 * - 12 digits: +XX(XXX) XXX-XXXX (e.g., +92(345) 678-7654)
 * - 13 digits: +XXX(XXX) XXX-XXXX (3-digit country code)
 */
export function formatPhoneNumber(phone: string | undefined): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 12) {
    return `+${digits.slice(0, 2)}(${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`
  }
  if (digits.length === 13) {
    return `+${digits.slice(0, 3)}(${digits.slice(3, 6)}) ${digits.slice(6, 9)}-${digits.slice(9)}`
  }
  return phone
}

/**
 * Unformat phone number for API
 * @param phone - Formatted phone number
 * @returns Raw 10 digit phone number
 */
export function unformatPhoneNumber(phone: string | undefined): string {
  if (!phone) return ''
  return phone.replace(/\D/g, '')
}

/**
 * Mask SSN for display (show only last 4)
 * @param ssn - Full SSN
 * @returns Masked SSN
 */
export function maskSSN(ssn: string | undefined): string {
  if (!ssn) return ''
  const digits = ssn.replace(/\D/g, '')
  if (digits.length === 9) {
    return `XXX-XX-${digits.slice(5)}`
  }
  return ssn
}

/**
 * Format date for API (MM/DD/YYYY)
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export function formatDateForAPI(date: string | Date): string {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''

  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const year = dateObj.getFullYear()

  return `${month}/${day}/${year}`
}

/**
 * Parse API date to Date object
 * @param dateString - Date string from API
 * @returns Date object or null
 */
export function parseAPIDate(dateString: string | undefined): Date | null {
  if (!dateString) return null

  // Handle "1990-01-15 00:00:00" format
  const cleanDate = dateString.split(' ')[0]

  let year: number, month: number, day: number

  // Handle MM/DD/YYYY format (e.g., "01/11/2000")
  if (cleanDate.includes('/')) {
    const parts = cleanDate.split('/')
    if (parts.length === 3) {
      month = parseInt(parts[0], 10)
      day = parseInt(parts[1], 10)
      year = parseInt(parts[2], 10)
    } else {
      return null
    }
  } else if (cleanDate.includes('-')) {
    // Handle YYYY-MM-DD format
    const parts = cleanDate.split('-')
    if (parts.length === 3) {
      year = parseInt(parts[0], 10)
      month = parseInt(parts[1], 10)
      day = parseInt(parts[2], 10)
    } else {
      return null
    }
  } else {
    return null
  }

  // Use local time constructor to avoid UTC timezone shift
  // new Date('YYYY-MM-DD') parses as UTC, causing off-by-one in positive timezones
  const date = new Date(year!, month! - 1, day!)

  return isNaN(date.getTime()) ? null : date
}

/**
 * Format API date for input field (YYYY-MM-DD)
 * @param dateString - Date string from API
 * @returns Formatted date for input
 */
export function formatDateForInput(dateString: string | undefined): string {
  const date = parseAPIDate(dateString)
  if (!date) return ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

